<script setup lang="ts">
const props = defineProps({
	isShow: {
		type: Boolean,
		default: false,
		required: true,
	},
});

defineEmits<{
	(e: 'onClose'): void
	(e: 'onOpenDialog'): void
}>();


const links = [
	{
		name: 'How can I order a ticket?',
		link: 'https://us-help-attendee.peatix.com/en/support/solutions/articles/44002536655-how-to-order-tickets',
	},
	{
		name: 'Where can I find my tickets?',
		link: 'https://us-help-attendee.peatix.com/en/support/solutions/articles/44002536658-how-to-access-your-tickets',
	},
	{
		name: 'How can I join this event?',
		link: 'https://us-help-attendee.peatix.com/en/support/solutions/articles/44002536660-how-to-join-events',
	},
];

const isShowValue = computed(() => props.isShow);
</script>

<template>
  <BaseDialog
    class="contact-dialog"
    :is-show="isShowValue"
    @on-close="$emit('onClose')"
  >
    <template #header>
      <h1 class="text-lg text-semibold">
        Contact us
      </h1>
    </template>
    <template #content>
      <div class="contact-content">
        <div class="link-container">
          <h2 class="common-faq-header text-md text-semibold">
            Frequently asked questions
          </h2>
          <ul class="common-faq-links">
            <li
              v-for="(link, index) in links"
              :key="index"
            >
              <a
                :href="link.link"
                class="faq-link"
                target="_blank"
                rel="noopener noreferrer"
              >{{ link.name }}</a>
            </li>
          </ul>
        </div>
        <div class="btn-container">
          <RouterLink
            :to="{ name: ROUTE.HELP.NAME }"
            target="_blank"
          >
            <BaseButton
              class="contact-button w-full"
              variant="outlined"
              color="primary"
            >
              <MessageAlertCircleIcon
                class="icon-header-confirm-dlg icon-info"
                width="14"
                height="14"
                :color="ICON_COLOR.PRIMARY"
              />
              <p class="btn-title text-md text-semibold text-left">
                Visit Help Center
              </p>
              <p class="btn-desc text-sm text-regular text-left">
                Access more general information about your Peatix account.
              </p>
            </BaseButton>
          </RouterLink>
          <BaseButton
            class="contact-button w-full"
            variant="outlined"
            color="primary"
            @click="$emit('onOpenDialog')"
          >
            <MailIcon
              class="icon-header-confirm-dlg icon-info"
              width="14"
              height="14"
              :color="ICON_COLOR.PRIMARY"
            />
            <p class="btn-title text-md text-semibold text-left">
              Message organizer about this event
            </p>
            <p class="btn-desc text-sm text-regular text-left">
              For help with event specific questions, such as "Is there a dress code?".
            </p>
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<style scoped lang="scss">
.contact-dialog {
  color: colors-get(gray, 900);

  .contact-content {
    display: flex;
    flex-direction: column;
    row-gap: spacings-get(5);
  }

  .link-container {
    display: flex;
    flex-direction: column;
    gap: spacings-get(3);
  }
}

.common-faq {
  color: colors-get(gray, 900);

  &-links {
    display: flex;
    flex-direction: column;
    gap: spacings-get(2);
    list-style: none;

    .faq-link {
      @include fonts-get(semibold, text-sm);
      color: colors-get(primary, 600);
      text-decoration: none;
    }
  }
}

.btn-container {
  display: flex;
  flex-direction: column;
  gap: spacings-get(4);

  .contact-button {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    row-gap: spacings-get(2);
    padding: spacings-get(3);
    background-color: colors-get(base, white) !important;
    border-color: colors-get(gray, 200);

    &:hover {
      border-color: colors-get(primary, 300);
    }
  }

  :deep(.icon-header-confirm-dlg) {
    padding: rem(5);
    border-width: spacings-get(1);
  }

  .btn-title {
    color: colors-get(gray, 900);
  }

  .btn-desc {
    color: colors-get(gray, 600);
  }
}
</style>
